const decodeContentDispositionFilename = (rawValue: string, isEncoded: boolean): string | null => {
	const unquotedValue = rawValue.replace(/^["']|["']$/g, "").trim();
	if (!unquotedValue) {
		return null;
	}
	let decodedValue = unquotedValue;
	if (isEncoded) {
		const valuePart = unquotedValue.includes("''") ? unquotedValue.split("''").slice(1).join("''") : unquotedValue;
		try {
			decodedValue = decodeURIComponent(valuePart);
		} catch {
			decodedValue = valuePart;
		}
	}
	const normalizedFilename = decodedValue.split(/[\\/]/).pop()?.trim();
	if (!normalizedFilename) {
		return null;
	}
	const safeFilename = normalizedFilename.replace(/[\u0000-\u001f\u007f]/g, "").trim();
	return safeFilename || null;
};
const normalizeFilename = (rawFilename: string | null): string | null => {
	if (!rawFilename) {
		return null;
	}
	return decodeContentDispositionFilename(rawFilename, false);
};
const normalizeFileExtension = (rawFileType: string | null): string | null => {
	if (!rawFileType) {
		return null;
	}
	const fileTypeValue = rawFileType.trim().toLowerCase();
	if (!fileTypeValue) {
		return null;
	}
	const mimeCandidate = fileTypeValue.split(";")[0];
	const extCandidate = mimeCandidate.includes("/") ? mimeCandidate.split("/").pop() : mimeCandidate;
	const cleanedExtension = extCandidate?.replace(/^\.+/, "").replace(/[^a-z0-9]/g, "");
	return cleanedExtension || null;
};
const ensureFilenameExtension = (filename: string, extension: string | null): string => {
	if (!extension) {
		return filename;
	}
	if (filename.toLowerCase().endsWith(`.${extension}`)) {
		return filename;
	}
	return filename.includes(".") ? filename : `${filename}.${extension}`;
};

const extractDispositionParam = (headerValue: string, key: "filename" | "filename*"): string | null => {
	const escapedKey = key === "filename*" ? "filename\\*" : "filename";
	const match = headerValue.match(new RegExp(`${escapedKey}\\s*=\\s*(?:"([^"]+)"|'([^']+)'|([^;,\\n]+))`, "i"));
	if (!match) {
		return null;
	}
	return (match[1] ?? match[2] ?? match[3] ?? "").trim() || null;
};

export const extractFilenameFromContentDisposition = (contentDisposition: string | null): string | null => {
	if (!contentDisposition) {
		return null;
	}
	const encodedValue = extractDispositionParam(contentDisposition, "filename*");
	if (encodedValue) {
		const filename = decodeContentDispositionFilename(encodedValue, true);
		if (filename) {
			return filename;
		}
	}
	const filenameValue = extractDispositionParam(contentDisposition, "filename");
	if (!filenameValue) {
		return null;
	}
	return decodeContentDispositionFilename(filenameValue, false);
};

export const extractFilenameFromExportHeaders = (headers: { get(name: string): string | null }): string | null => {
	const exportFilename = normalizeFilename(headers.get("x-export-filename"));
	const exportFileType = normalizeFileExtension(headers.get("x-export-filetype"));
	if (exportFilename) {
		return ensureFilenameExtension(exportFilename, exportFileType);
	}
	return extractFilenameFromContentDisposition(headers.get("content-disposition"));
};
